import { computed, inject, Injectable, signal } from '@angular/core';
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  User,
  UserCredential,
} from 'firebase/auth';

import { FIREBASE_AUTH } from '../firebase/firebase';

/**
 * Wraps Firebase Authentication behind a small, signal-based API.
 *
 * The current user is exposed as a signal so templates and guards react to
 * sign-in/out without RxJS plumbing — a good fit for this zoneless app.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly auth = inject(FIREBASE_AUTH);

  private readonly currentUser = signal<User | null>(null);
  private readonly initialized = signal(false);

  /** The signed-in Firebase user, or `null`. */
  readonly user = this.currentUser.asReadonly();
  /** `true` once Firebase has reported the initial auth state. */
  readonly isReady = this.initialized.asReadonly();
  readonly isAuthenticated = computed(() => this.currentUser() !== null);

  /** Resolves after the first `onAuthStateChanged` callback. Used by the guard. */
  readonly ready: Promise<void>;

  constructor() {
    this.ready = new Promise<void>((resolve) => {
      onAuthStateChanged(this.auth, (user) => {
        this.currentUser.set(user);
        if (!this.initialized()) {
          this.initialized.set(true);
          resolve();
        }
      });
    });
  }

  /** The current user's Firebase ID token (for API calls), or `null`. */
  getIdToken(): Promise<string | null> {
    const user = this.auth.currentUser;
    return user ? user.getIdToken() : Promise.resolve(null);
  }

  signInWithGoogle(): Promise<UserCredential> {
    return signInWithPopup(this.auth, new GoogleAuthProvider());
  }

  signInWithEmail(email: string, password: string): Promise<UserCredential> {
    return signInWithEmailAndPassword(this.auth, email, password);
  }

  registerWithEmail(email: string, password: string): Promise<UserCredential> {
    return createUserWithEmailAndPassword(this.auth, email, password);
  }

  signOut(): Promise<void> {
    return signOut(this.auth);
  }
}
